import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Stethoscope, Shield, Clock3, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-medical-clinic.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIForMedicalClinics = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiForMedicalClinics.metaTitle')}</title>
        <meta name="description" content={t('blog.aiForMedicalClinics.metaDescription')} />
        <meta name="keywords" content="AI for medical clinics, healthcare AI phone agent, HIPAA compliant AI, medical appointment scheduling AI, clinic virtual receptionist" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-for-medical-clinics" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-for-medical-clinics" />
        <meta property="og:title" content={t('blog.aiForMedicalClinics.title')} />
        <meta property="og:description" content={t('blog.aiForMedicalClinics.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiForMedicalClinics.title'),
            "description": t('blog.aiForMedicalClinics.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI" },
            "datePublished": "2025-08-28",
            "dateModified": "2025-08-28"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiForMedicalClinics.title')}
        category={t('blog.categories.healthcare')}
        date="August 28, 2025"
        readTime={`8 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI phone agents for medical clinics"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiForMedicalClinics.intro')}
        </p>

        <div className="grid md:grid-cols-4 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Stethoscope className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">35%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForMedicalClinics.stat1')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">HIPAA</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForMedicalClinics.stat2')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Clock3 className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">70%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForMedicalClinics.stat3')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Phone className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">24/7</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForMedicalClinics.stat4')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForMedicalClinics.challengeTitle')}</h2>
        <p>{t('blog.aiForMedicalClinics.challengeDesc1')}</p>
        <p className="mt-4">{t('blog.aiForMedicalClinics.challengeDesc2')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForMedicalClinics.hipaaTitle')}</h2>
        <p>{t('blog.aiForMedicalClinics.hipaaIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.aiForMedicalClinics.hipaa1')}</li>
          <li>{t('blog.aiForMedicalClinics.hipaa2')}</li>
          <li>{t('blog.aiForMedicalClinics.hipaa3')}</li>
          <li>{t('blog.aiForMedicalClinics.hipaa4')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForMedicalClinics.useCasesTitle')}</h2>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForMedicalClinics.useCase1Title')}</h3>
        <p>{t('blog.aiForMedicalClinics.useCase1Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForMedicalClinics.useCase2Title')}</h3>
        <p>{t('blog.aiForMedicalClinics.useCase2Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForMedicalClinics.useCase3Title')}</h3>
        <p>{t('blog.aiForMedicalClinics.useCase3Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForMedicalClinics.useCase4Title')}</h3>
        <p>{t('blog.aiForMedicalClinics.useCase4Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForMedicalClinics.integrationTitle')}</h2>
        <p>{t('blog.aiForMedicalClinics.integrationIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.aiForMedicalClinics.integration1')}</li>
          <li>{t('blog.aiForMedicalClinics.integration2')}</li>
          <li>{t('blog.aiForMedicalClinics.integration3')}</li>
          <li>{t('blog.aiForMedicalClinics.integration4')}</li>
        </ul>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiForMedicalClinics.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiForMedicalClinics.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiForMedicalClinics.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIForMedicalClinics;
